import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    ADDITIONAL_SERVICES_TYPE,
    ADDITIONAL_SERVICES_USED_FOR,
} from 'src/features/parkingManagement/types/additionalServices';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import { RESERVATION_TYPE } from 'src/features/reservations/types/reservationType';
import { reservationMessages } from 'src/features/reservations/utils/messages';
import subscriptionMessages from 'src/features/subscriptions/utils/messages';
import { DialogTitle } from 'src/registry/ui/Dialog';
import { FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import {
    MultiSelect,
    MultiSelectContent,
    MultiSelectControl,
    MultiSelectOption,
    MultiSelectTrigger,
} from 'src/registry/ui/MultiSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { Separator } from 'src/registry/ui/Separator';
import { generalMessages } from 'src/utils/messages';

const AdditionalServicesDetails = () => {
    const { control, setValue, watch } = useFormContext();
    const { formatMessage } = useIntl();

    const serviceTypeMapping = {
        [ADDITIONAL_SERVICES_TYPE.SERVICE_CHARGE]: parkingManagementMessages.serviceCharge,
        [ADDITIONAL_SERVICES_TYPE.CANCELLATION_OPTION]: parkingManagementMessages.cancellationOption,
    };

    const serviceUsedForMapping = {
        [ADDITIONAL_SERVICES_USED_FOR.RESERVATION]: reservationMessages.reservation,
        [ADDITIONAL_SERVICES_USED_FOR.SUBSCRIPTION]: subscriptionMessages.subscription,
        [ADDITIONAL_SERVICES_USED_FOR.VEHICLE_STAY]: parkingManagementMessages.vehicleStay,
    };

    const optionalServiceMapping = {
        [formatMessage(generalMessages.yes)]: 'true',
        [formatMessage(generalMessages.no)]: 'false',
    };
    const disableReservationType =
        watch('used_for') !== undefined && watch('used_for') !== ADDITIONAL_SERVICES_USED_FOR.RESERVATION;

    return (
        <>
            <DialogTitle>
                <FormattedMessage {...parkingManagementMessages.serviceDetails} />
            </DialogTitle>
            <Separator className="mb-4" />

            <FormField
                control={control}
                name="type"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...parkingManagementMessages.type} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select
                                    value={(field.value as ADDITIONAL_SERVICES_TYPE) || ''}
                                    onValueChange={field.onChange}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue
                                            placeholder={formatMessage(parkingManagementMessages.selectType)}
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(serviceTypeMapping).map(([key, value]) => (
                                            <SelectItem key={key} value={key}>
                                                {formatMessage(value)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <FormField
                control={control}
                name="used_for"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...parkingManagementMessages.module} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select
                                    value={(field.value as ADDITIONAL_SERVICES_USED_FOR) || ''}
                                    onValueChange={field.onChange}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue
                                            placeholder={formatMessage(parkingManagementMessages.selectModule)}
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(serviceUsedForMapping).map(([key, value]) => (
                                            <SelectItem key={key} value={key}>
                                                {formatMessage(value)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={control}
                name="reservation_types"
                render={({ field }) => {
                    const { ref: _, ...rest } = field;
                    const fieldData = (field.value as RESERVATION_TYPE[])
                        .map((type) => {
                            const label = Object.values(RESERVATION_TYPE).find((p) => p === type);
                            return label ? formatMessage(reservationMessages[label]) : null;
                        })
                        .join(', ');

                    const allValues = Object.values(RESERVATION_TYPE).map((type) => type);
                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog">
                                <FormattedMessage {...reservationMessages.reservationType} />
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <MultiSelect {...rest}>
                                        <MultiSelectTrigger asChild>
                                            <MultiSelectControl
                                                className="-mr-4 w-full"
                                                disabled={disableReservationType}
                                            >
                                                {fieldData}
                                                {fieldData.length == 0 && (
                                                    <span className="text-gray-400">
                                                        <FormattedMessage
                                                            {...reservationMessages.selectReservationType}
                                                        />
                                                    </span>
                                                )}
                                            </MultiSelectControl>
                                        </MultiSelectTrigger>
                                        <MultiSelectContent allValues={allValues}>
                                            {Object.values(RESERVATION_TYPE).map((type) => (
                                                <MultiSelectOption key={type} selected={type}>
                                                    <div className="flex flex-row justify-between">
                                                        {formatMessage(reservationMessages[type])}
                                                    </div>
                                                </MultiSelectOption>
                                            ))}
                                        </MultiSelectContent>
                                    </MultiSelect>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />

            <FormField
                control={control}
                name="is_optional"
                render={() => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">
                            <FormattedMessage {...parkingManagementMessages.optional} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select
                                    onValueChange={(val) =>
                                        setValue('is_optional', val === 'true', {
                                            shouldDirty: true,
                                        })
                                    }
                                    value={watch('is_optional') === undefined ? '' : String(watch('is_optional'))}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={formatMessage(generalMessages.selectValue)} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(optionalServiceMapping).map(([key, value]) => {
                                            return (
                                                <SelectItem key={key} value={value}>
                                                    {key}
                                                </SelectItem>
                                            );
                                        })}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <FormField
                control={control}
                name="is_enabled"
                render={() => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">
                            <FormattedMessage {...parkingManagementMessages.enabled} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select
                                    onValueChange={(val) =>
                                        setValue('is_enabled', val === 'true', {
                                            shouldDirty: true,
                                        })
                                    }
                                    value={watch('is_enabled') === undefined ? '' : String(watch('is_enabled'))}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={formatMessage(generalMessages.selectValue)} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(optionalServiceMapping).map(([key, value]) => {
                                            return (
                                                <SelectItem key={key} value={value}>
                                                    {key}
                                                </SelectItem>
                                            );
                                        })}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
        </>
    );
};

export default AdditionalServicesDetails;
